import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { CheckCircle2, AlertTriangle, Info, X } from 'lucide-react';
import { sharedText, useUiLocale, type UiLocale } from './i18n';

type Tone = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  tone: Tone;
  title: string;
  body?: string;
  /** Locale of the screen that raised it; the region itself sits outside any shell. */
  locale: UiLocale;
}

interface ToastApi {
  success: (title: string, body?: string) => void;
  error: (title: string, body?: string) => void;
  info: (title: string, body?: string) => void;
}

type Push = (tone: Tone, title: string, body: string | undefined, locale: UiLocale) => void;

const ToastContext = createContext<Push | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => setToasts((list) => list.filter((t) => t.id !== id)), []);

  const push = useCallback(
    (tone: Tone, title: string, body: string | undefined, locale: UiLocale) => {
      const id = nextId.current++;
      setToasts((list) => [...list.slice(-3), { id, tone, title, body, locale }]);
      window.setTimeout(() => dismiss(id), tone === 'error' ? 9000 : 4500);
    },
    [dismiss],
  );

  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="toast-region" aria-live="polite" aria-relevant="additions">
        {toasts.map((toast) => {
          const Icon = toast.tone === 'success' ? CheckCircle2 : toast.tone === 'error' ? AlertTriangle : Info;
          return (
            <div key={toast.id} className={`toast toast--${toast.tone}`} role={toast.tone === 'error' ? 'alert' : 'status'}>
              <Icon size={18} aria-hidden="true" className="toast__icon" />
              <div className="toast__text">
                <strong>{toast.title}</strong>
                {toast.body ? <span>{toast.body}</span> : null}
              </div>
              <button type="button" className="icon-button icon-button--quiet" onClick={() => dismiss(toast.id)} aria-label={sharedText(toast.locale).dismissNotification}>
                <X size={16} aria-hidden="true" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const push = useContext(ToastContext);
  const locale = useUiLocale();
  const api = useMemo<ToastApi | null>(
    () =>
      push && {
        success: (title, body) => push('success', title, body, locale),
        error: (title, body) => push('error', title, body, locale),
        info: (title, body) => push('info', title, body, locale),
      },
    [push, locale],
  );
  if (!api) throw new Error('useToast must be used inside ToastProvider');
  return api;
}
